
import { SimulationConfig } from '../types';

export class AudioSlicer {
  private songBuffer: AudioBuffer | null = null;
  private songCursor: number = 0;
  private lastScheduledEndTime: number = 0;
  private sequenceIndex: number = 0;
  private lastCollisionTime: number = 0;

  constructor() {}

  public setBuffer(buffer: AudioBuffer) {
      this.songBuffer = buffer;
      this.reset();
  }

  public reset() {
      this.songCursor = 0;
      this.lastScheduledEndTime = 0;
      this.sequenceIndex = 0;
  }

  public getDuration(): number {
      return this.songBuffer?.duration || 0;
  }

  public getPeaks(threshold: number = 0.5): number[] {
      if (!this.songBuffer) return [];
      const data = this.songBuffer.getChannelData(0);
      const step = Math.floor(this.songBuffer.sampleRate * 0.05);
      const peaks: number[] = [];
      
      for (let i = 0; i < data.length; i += step) {
          const slice = data.slice(i, i + step);
          const max = Math.max(...slice.map(Math.abs));
          if (max > threshold) {
              peaks.push(i / this.songBuffer.sampleRate);
          }
      }
      
      const filtered: number[] = [];
      let lastTime = -1;
      for (const p of peaks) {
          if (p - lastTime > 0.1) {
              filtered.push(p);
              lastTime = p;
          }
      }
      return filtered;
  }

  public calculateNextSlice(config: SimulationConfig, now: number): { 
      startTimeOffset: number, 
      actualDuration: number, 
      playStartTime: number,
      playbackRate: number 
  } | null {
      if (!this.songBuffer || config.audioMutedInSlicer) return null;
      
      if (config.songResetEnabled) {
          const timeSinceLast = now - this.lastCollisionTime;
          if (timeSinceLast > config.songResetTimer) {
              this.songCursor = 0;
              this.sequenceIndex = 0;
              this.lastScheduledEndTime = 0;
          }
      }
      this.lastCollisionTime = now;

      let playbackRate = 1.0;
      const queueAhead = this.lastScheduledEndTime - now;
      const baseSliceDuration = config.songSliceDuration || 0.15;
      
      if (queueAhead > baseSliceDuration) {
          return null;
      }

      if (config.songSpeedIncreaseEnabled) {
          if (queueAhead > 0) {
              playbackRate = Math.min(config.songSpeedMax || 2.0, 1.0 + (queueAhead * 2));
          }
      }

      let baseDuration = baseSliceDuration;
      let startTimeOffset = 0;

      if (config.songSliceSequence && config.songSliceSequence.length > 0) {
          const rawSlice = config.songSliceSequence[this.sequenceIndex % config.songSliceSequence.length];
          const slice = typeof rawSlice === 'number' ? { start: 0, duration: rawSlice } : rawSlice;
          baseDuration = slice.duration;
          startTimeOffset = slice.start;
          this.sequenceIndex++;
      } else {
          if (this.songCursor >= this.songBuffer.duration) {
              this.songCursor = 0;
          }
          startTimeOffset = this.songCursor;
          this.songCursor += baseDuration;
      }

      baseDuration = Math.max(0.01, baseDuration);
      const actualDuration = baseDuration / playbackRate;
      const playStartTime = Math.max(now, this.lastScheduledEndTime);
      
      this.lastScheduledEndTime = playStartTime + actualDuration;
      
      return {
          startTimeOffset: startTimeOffset % this.songBuffer.duration,
          actualDuration,
          playStartTime,
          playbackRate
      };
  }

  public getSliceAtIndex(index: number, config: SimulationConfig): { startTime: number, duration: number } | null {
      if (!this.songBuffer || !config.songSliceSequence || config.songSliceSequence.length === 0) return null;
      
      const rawSlice = config.songSliceSequence[index % config.songSliceSequence.length];
      const slice = typeof rawSlice === 'number' ? { start: 0, duration: rawSlice } : rawSlice;
      
      return {
          startTime: slice.start % this.songBuffer.duration,
          duration: slice.duration
      };
  }
}
